import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

export default function AboutPage() {
  const { settings } = useTheme();

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        {settings.about_image && (
          <img
            src={settings.about_image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-primary)]/80 via-[var(--color-primary)]/60 to-black/70" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
            Our Story
          </h1>
          <p className="mt-6 text-lg text-gray-200 max-w-2xl mx-auto">
            Born from a love of heat, cold water, and the Okanagan outdoors.
          </p>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20 bg-[var(--color-bg)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[var(--color-text)] mb-6">
                The Okanagan Sauna Experience
              </h2>
              <p className="text-[var(--color-text-light)] leading-relaxed mb-6">
                {settings.about_text || 'Skaha Sauna Co. brings the authentic Nordic sauna experience to the Okanagan. Our custom-built trailer sauna is delivered right to your chosen location \u2014 whether that\'s lakeside at Skaha Beach, a mountain trailhead, or your own backyard.'}
              </p>
              <p className="text-[var(--color-text-light)] leading-relaxed mb-6">
                Our roots are in the Finnish sauna tradition \u2014 a practice that has been central to Nordic life for thousands of years. We believe in the transformative power of heat and cold. There is nothing quite like stepping out of a wood-fired sauna and plunging into the crisp waters of Skaha Lake, surrounded by the vineyards and mountains of the South Okanagan.
              </p>
              <p className="text-[var(--color-text-light)] leading-relaxed">
                Our custom-built trailer sauna is designed to deliver that authentic experience anywhere. Handcrafted with cedar interior, a premium wood-fired stove, and seating for up to eight guests, our sauna reaches temperatures that rival the best saunas in Scandinavia. And we bring it right to you.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={settings.about_image || '/uploads/placeholder.webp'}
                  alt="Skaha Sauna Co. outdoor sauna"
                  className="w-full h-[400px] object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 rounded-xl overflow-hidden shadow-lg border-4 border-[var(--color-bg)] hidden lg:block">
                <img
                  src={settings.hero_image || '/uploads/placeholder.webp'}
                  alt="Lake scenery"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Special */}
      <section className="py-20 bg-[var(--color-bg-alt)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[var(--color-text)] mb-14">
            What Makes Us Different
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[var(--color-accent)] bg-opacity-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-[var(--color-accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 21v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21m0 0h4.5V3.545M12.75 21h7.5V10.75M2.25 21h1.5m18 0h-18M2.25 9l4.5-1.636M18.75 3l-1.5.545m0 6.205l3 1m1.5.5l-1.5-.5M6.75 7.364V3h-3v18m3-13.636l10.5-3.819" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[var(--color-text)] mb-3">Custom Trailer Build</h3>
              <p className="text-[var(--color-text-light)] leading-relaxed">
                Our sauna trailer is purpose-built for the Okanagan. Cedar-lined interior, premium wood stove, and a design that handles summer heat and winter snow equally well.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[var(--color-accent)] bg-opacity-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-[var(--color-accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1A3.75 3.75 0 0012 18z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[var(--color-text)] mb-3">Finnish Tradition</h3>
              <p className="text-[var(--color-text-light)] leading-relaxed">
                We follow the authentic Finnish sauna practice. Real wood fire, proper steam (loyly), and the contrast of hot and cold that has been central to Nordic wellness for millennia.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[var(--color-accent)] bg-opacity-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-[var(--color-accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[var(--color-text)] mb-3">Okanagan Location</h3>
              <p className="text-[var(--color-text-light)] leading-relaxed">
                Penticton sits between two lakes with mountains on every side. There is no better backdrop for a sauna session. We deliver to locations throughout the South Okanagan.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-[var(--color-bg)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[var(--color-text)] mb-10 text-center">
            What to Expect
          </h2>
          <div className="space-y-8">
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--color-accent)] text-white flex items-center justify-center font-bold text-lg">
                1
              </div>
              <div>
                <h3 className="text-lg font-bold text-[var(--color-text)] mb-2">We Set Up</h3>
                <p className="text-[var(--color-text-light)] leading-relaxed">
                  We arrive at your location, park the trailer, fire up the wood stove, and have everything ready before your session begins. All you need to do is show up.
                </p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--color-accent)] text-white flex items-center justify-center font-bold text-lg">
                2
              </div>
              <div>
                <h3 className="text-lg font-bold text-[var(--color-text)] mb-2">You Sweat</h3>
                <p className="text-[var(--color-text-light)] leading-relaxed">
                  Step inside, throw some water on the hot stones, and feel the wave of steam fill the room. The sauna reaches 80-100 degrees Celsius. Alternate between the heat and a cold plunge or the fresh Okanagan air.
                </p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--color-accent)] text-white flex items-center justify-center font-bold text-lg">
                3
              </div>
              <div>
                <h3 className="text-lg font-bold text-[var(--color-text)] mb-2">We Clean Up</h3>
                <p className="text-[var(--color-text-light)] leading-relaxed">
                  When your session is done, we handle everything. Cleanup, teardown, and towing the trailer away. You leave feeling refreshed with zero hassle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[var(--color-bg-alt)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)] mb-4">
            Ready to Experience It?
          </h2>
          <p className="text-[var(--color-text-light)] mb-8">
            Book your session and find out what the heat is all about.
          </p>
          <Link
            to="/book"
            className="inline-block px-8 py-4 bg-[var(--color-accent)] text-white text-lg font-semibold rounded-xl hover:opacity-90 transition-opacity"
          >
            Book Your Session
          </Link>
        </div>
      </section>
    </div>
  );
}
